import Vue from 'vue'
import api from '@services/api'
import router from '@router'
import { isDisplayScreen } from '@mixins/reloads-on-publish'
import { getField, updateField } from 'vuex-map-fields'
import {
  SET_AUTH_SNACKBAR,
  TOGGLE_AUTH_SNACKBAR,
  SET_CURRENT_USER,
  USER_REFRESH,
  SET_AUTH_MODAL,
  SET_LOG_OUT_CONFIRM,
  SET_EDITS_AWAITING_LOG_IN,
} from '@constants/mutations'

// Resolves the promise returned by requestLogOut once the owner answers
let resolveLogOutConfirm = null

export const state = {
  // currentUser: getSavedState('auth.currentUser'),
  // currentUser: {
  //   email: 'admin@joinresto.com',
  //   token: 'zLg5QEkdQNs2TgyGHuB62I4xE45ano6shKqEba2taU5JTCk1trX1ASMLuV7c',
  // },
  autosave: false,
  isAuthSnackbarVisible: false,
  isAuthModalVisible: false,
  isLoginSuccessSnackbarVisible: false,
  isLogOutConfirmVisible: false,
  hasEditsAwaitingLogIn: false,
}

export const mutations = {
  [SET_CURRENT_USER](state, newValue) {
    Vue.set(state, 'currentUser', newValue)
    saveState('auth.currentUser', newValue)
    setDefaultAuthHeaders(state)
    // Only on a login: validate also clears the user after a forced logout
    if (newValue) state.hasEditsAwaitingLogIn = false
  },

  [USER_REFRESH](state, userData) {
    let updatedUser = { ...state.currentUser, ...userData }
    Vue.set(state, 'currentUser', updatedUser)
    saveState('auth.currentUser', updatedUser)
    setDefaultAuthHeaders(state)
    // validate's 200 can land after an old-token 401 and log back in
    state.hasEditsAwaitingLogIn = false
  },

  [TOGGLE_AUTH_SNACKBAR](state) {
    state.isAuthSnackbarVisible = !state.isAuthSnackbarVisible
  }, // TOGGLE_AUTH_SNACKBAR

  [SET_AUTH_SNACKBAR](state, isVisible) {
    state.isAuthSnackbarVisible = isVisible
  },

  [SET_AUTH_MODAL](state, isVisible) {
    state.isAuthModalVisible = isVisible
  },

  [SET_LOG_OUT_CONFIRM](state, isVisible) {
    state.isLogOutConfirmVisible = isVisible
  },

  [SET_EDITS_AWAITING_LOG_IN](state, isAwaiting) {
    state.hasEditsAwaitingLogIn = isAwaiting
  },
  updateField,
}

export const getters = {
  loggedIn: (state) => !!state.currentUser && !!state.currentUser.token,
  getUser: (state) => state.currentUser,

  isAuthSnackbarVisible: (state) => state.isAuthSnackbarVisible,
  isAuthModalVisible: (state) => state.isAuthModalVisible,
  isLogOutConfirmVisible: (state) => state.isLogOutConfirmVisible,
  hasEditsAwaitingLogIn: (state) => state.hasEditsAwaitingLogIn,
  getField,
}

export const actions = {
  // This is automatically run in `src/state/store.js` when the app
  // starts, along with any other actions named `init` in other modules.
  init({ state, dispatch }) {
    setDefaultAuthHeaders(state)
    // dispatch('validate')
  },

  // Logs in the current user.
  logIn({ commit, dispatch, getters }, { email, password } = {}) {
    if (getters.loggedIn) return dispatch('validate')

    return api.post('/login', { email, password }).then((response) => {
      const user = response.data
      commit(SET_CURRENT_USER, user)
      dispatch('hideAuthSnackbar')
      return user
    })
  },

  // Logs out the current user.
  logOut({ commit, dispatch }) {
    commit(SET_CURRENT_USER, null)
    dispatch('showAuthSnackbar')
  },

  // Logs out when the API rejects the token. Nobody may be at a display screen
  // to answer a login prompt, so it stays quiet there, and an owner with
  // unpublished changes gets back in from the Login button on MenuSnackbar.
  forceLogOut({ commit, dispatch, getters, rootGetters }) {
    // A wrong password on POST /login also lands here, already logged out
    if (getters.loggedIn && rootGetters['editor/hasUnpublishedChanges']) {
      commit(SET_EDITS_AWAITING_LOG_IN, true)
    }
    const isShowingLayoutEditUI = rootGetters['layoutEditor/isShowingLayoutEditUI']
    const isOnDisplay = isDisplayScreen({
      routeName: router.currentRoute.name,
      isShowingLayoutEditUI,
      isShowingEditUI: rootGetters['editor/isShowingEditUI'],
    })
    // Logged out, the sidebar only has its Edit Layout button left. One opened
    // while logged out stays, since that button is how the owner logs in
    if (getters.loggedIn && isShowingLayoutEditUI) {
      dispatch('layoutEditor/setLayoutEditUI', false, { root: true })
    }
    if (!isOnDisplay) {
      dispatch('logOut')
      dispatch('showAuthModal')
      dispatch('showAuthSnackbar')
      return
    }
    commit(SET_CURRENT_USER, null)
    if (rootGetters['editor/hasUnpublishedChanges']) {
      dispatch('editor/showMenuSnackbar', null, { root: true })
    }
  },

  // Logs out from a Log Out button, asking first when there are
  // unpublished changes. Resolves to whether the user was logged out.
  requestLogOut({ commit, dispatch, rootGetters }) {
    if (!rootGetters['editor/hasUnpublishedChanges']) {
      dispatch('logOut')
      return Promise.resolve(true)
    }
    commit(SET_LOG_OUT_CONFIRM, true)
    return new Promise((resolve) => {
      resolveLogOutConfirm = resolve
    })
  },

  answerLogOutConfirm({ commit, dispatch }, confirmed) {
    commit(SET_LOG_OUT_CONFIRM, false)
    if (confirmed) {
      dispatch('layouts/discardElementChanges', null, { root: true })
      dispatch('logOut')
      // A 401 can land while the confirm is open. The owner has now chosen
      commit(SET_EDITS_AWAITING_LOG_IN, false)
    }
    if (resolveLogOutConfirm) resolveLogOutConfirm(confirmed)
    resolveLogOutConfirm = null
  },

  // Validates the current user's token and refreshes it
  // with new data from the API.
  validate({ commit, state }) {
    if (!state.currentUser) return Promise.resolve(null)

    return api
      .get('/validate-token')
      .then((response) => {
        const user = response.data
        commit(USER_REFRESH, user)
        return Promise.resolve(true)
      })
      .catch((error) => {
        if (error.response && error.response.status === 401) {
          commit(SET_CURRENT_USER, null)
        }
        return Promise.reject(error)
      })
  },

  // For an owner's click on a display screen, where forceLogOut stays quiet.
  // Automatic requests never come through here, so a TV is never prompted. Not
  // validate: GET /validate-token replaces the token, failing requests in flight
  promptLogInIfExpired({ dispatch, getters }, locationId) {
    return api.get(`/locations/${locationId}/layouts`).catch((error) => {
      if (error.response?.status === 401 && !getters.isAuthModalVisible) {
        dispatch('showAuthModal')
      }
    })
  }, // promptLogInIfExpired

  loginOrToggleUI({ dispatch, getters }) {
    if (getters.loggedIn) {
      dispatch('editor/toggleEditUI', null, { root: true })
    } else {
      dispatch('toggleAuthSnackbar')
    }
  }, // loginOrToggleUI

  toggleAuthSnackbar({ commit }) {
    commit(TOGGLE_AUTH_SNACKBAR)
  }, // toggleAuthSnackbar

  showAuthSnackbar({ commit }) {
    commit(SET_AUTH_SNACKBAR, true)
  }, // showAuthSnackbar

  hideAuthSnackbar({ commit }) {
    commit(SET_AUTH_SNACKBAR, false)
  }, // hideAuthSnackbar

  showAuthModal({ commit, dispatch }) {
    dispatch('hideAuthSnackbar')
    commit(SET_AUTH_MODAL, true)
  }, // showAuthModal

  hideAuthModal({ commit }) {
    commit(SET_AUTH_MODAL, false)
  }, // hideAuthModal
} // actions

// ===
// Private helpers
// ===

// function getSavedState(key) {
//   return JSON.parse(window.localStorage.getItem(key))
// }

function saveState(key, state) {
  window.localStorage.setItem(key, JSON.stringify(state))
}

function setDefaultAuthHeaders(state) {
  api.defaults.headers.common.Authorization = state.currentUser
    ? `Bearer ${state.currentUser.token}`
    : ''
}
